import * as React from "react";
import { useSelector } from "react-redux";
import { Button, Grid, Icon, Loader, Modal } from "semantic-ui-react";
import * as moment from "moment";

import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import { OtherActionTasks, taskActions } from "../../task/taskActions";
import { RebookAllButton } from "../../task/taskActions/RebookAllButton";
import { DidNotAttendAll } from "../../clinicalNotes/components/DidNotAttendAllButton";
import { appointmentIdSelector } from "../selectors";
import { isImmunisationTask } from "../../task/model";

import { MergedAppointmentDetails } from "./models";
import { AppointmentMergingApi } from "./AppointmentMergingApi";

interface MergedAppointmentModalOwnProps {
    onModalClose: () => void;
}

export const MergedAppointmentModal: React.FC<MergedAppointmentModalOwnProps> = (props) => {
    const [appointments, setAppointments] = React.useState<MergedAppointmentDetails[]>([]);
    const [cancelledAppointmentsDetails, setCancelledAppointmentsDetails] = React.useState<MergedAppointmentDetails[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const appointmentId = useSelector(appointmentIdSelector);

    const setMergedAppointments = React.useCallback(() => {
        setLoading(true);
        new AppointmentMergingApi().getMergedAppointments(appointmentId).then(response => { // eslint-disable-line promise/always-return
            setAppointments(response.mergedAppointmentDetails);
            setCancelledAppointmentsDetails(response.cancelledAppointmentsDetails);
            setLoading(false);
        });
    }, [appointmentId]);

    React.useEffect(() => {
        setMergedAppointments();
    }, [setMergedAppointments]);

    const cancelAppointment = (taskId: string) => {
        const modifiedAppointments = appointments.filter(a => a.task.id !== taskId);
        const cancelledAppointment = appointments.find(a => a.task.id === taskId);

        setAppointments(modifiedAppointments);
        setCancelledAppointmentsDetails(prevState => [...prevState, cancelledAppointment!]);
    };

    const caseViewButton = (appointment: MergedAppointmentDetails) => {
        return (
            <a
                href={`/customers/${appointment.task.customer.id}/employeeRecords/${appointment.task.employeeRecord.id}`
                    + `/ohCases/${appointment.task.caseId}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <Button content="View Case" />
            </a>
        );
    };

    const getImmunisationTypes = (taskModel: TaskModel): string => {
        if (isImmunisationTask(taskModel)) {
            const immsTypes = taskModel.immunisationTypeTasks.map(t => t.name);
            return immsTypes.join(",");
        }

        return "";
    };

    const taskDescription = (appointment: MergedAppointmentDetails): string => {
        return appointment.task.description + " - " + appointment.employeePersonalReferenceNumber +
            " " + getImmunisationTypes(appointment.task);
    };

    const incompleteOrCancelledAppointments = React.useMemo(
        () => appointments.filter(appointment => !appointment.task.completed && !appointment.task.cancelled),
        [appointments]
    );
    const tasks = React.useMemo(() => incompleteOrCancelledAppointments.map(appointment => appointment.task), [incompleteOrCancelledAppointments]);

    return (
        <>
            <Modal open size="large">
                <Modal.Header content={"Appointments"} />
                <Modal.Content>
                    {loading &&
                        <Loader active={loading} />
                    }
                    {!loading &&
                        <Grid>
                            {appointments.map((appointment) => {
                                return (
                                    <Grid.Row
                                        key={appointment.appointment.id}
                                        columns="equal"
                                        verticalAlign="middle">
                                        <Grid.Column
                                            width={7}
                                        >
                                            {taskDescription(appointment)}
                                        </Grid.Column>
                                        <Grid.Column width={6}>
                                            {appointment.task.actions && !appointment.task.cancelled &&
                                                appointment.task.actions.sort().filter(a => {
                                                    if (OtherActionTasks.includes(a) || a.startsWith("Start"))
                                                    {
                                                        return false;
                                                    }

                                                    return taskActions[a];
                                                }).map(a => (<div
                                                    key={a}>{taskActions[a](
                                                        appointment.task,
                                                        appointment.task.customer.id,
                                                        appointments.length !== 1, cancelAppointment)}
                                                </div>))}
                                        </Grid.Column>
                                        <Grid.Column textAlign="right">
                                            {caseViewButton(appointment)}
                                        </Grid.Column>
                                    </Grid.Row>
                                );
                            })
                            }
                            {cancelledAppointmentsDetails.map((appointment) => {
                                return (
                                    <Grid.Row
                                        key={appointment.appointment.id}
                                        columns="equal"
                                        verticalAlign="middle"
                                    >
                                        <Grid.Column width={13}>{appointment.task.description} -
                                                Cancelled</Grid.Column>
                                        <Grid.Column textAlign="right">
                                            {caseViewButton(appointment)}
                                        </Grid.Column>
                                    </Grid.Row>
                                );
                            })
                            }
                        </Grid>
                    }
                </Modal.Content>
                <Modal.Actions>
                    {!loading &&
                        <Grid columns="2">
                            <Grid.Column textAlign="left">
                                <DidNotAttendAll
                                    tasks={tasks}
                                    disabled={incompleteOrCancelledAppointments.length === 0}
                                    appointmentEndDate={incompleteOrCancelledAppointments.length > 0
                                        ? moment(incompleteOrCancelledAppointments[0].appointment.end) : moment()}
                                />
                                <RebookAllButton
                                    tasks={incompleteOrCancelledAppointments
                                        .map((appointment: MergedAppointmentDetails) => appointment.task)}
                                    disabled={incompleteOrCancelledAppointments.length === 0}
                                />
                                <Button icon onClick={setMergedAppointments}>
                                    <Icon name='refresh' />
                                </Button>
                            </Grid.Column>
                            <Grid.Column>
                                <Button content="Close" onClick={props.onModalClose} />
                            </Grid.Column>
                        </Grid>
                    }
                </Modal.Actions>
            </Modal>
        </>
    );
};
